import { useState, useMemo, useEffect } from "react";
import { KeycloakTokenParsed } from "keycloak-js";
import { toast } from 'react-toastify';

import { createProjectActionItems, updateProjectActionItems, deleteProjectActionItems } from "../../api/actionItems";


import { useUsersPage } from "../../state/users";
import { Avatar } from '../Avatar';
import { SelectUserAvatar } from '../SelectUserAvatar';
import { ActionItemsDocumentAttachment } from './ActionItemsDocumentAttachment';
import { AddActionItemModal } from './AddActionItemModal';

import { 
  FaPlus,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaCircle,
  FaTrash
} from 'react-icons/fa';

import { 
  getTableHeaderStyle, 
  getFormLabelStyle, 
  getTaskNameStyle, 
  getRiskColor, 
  getFormSelectStyle, 
  getFormSelectContainerStyle,
  DesignSystem,
  getOriginalStatusSelectStyle,
  getOriginalAssigneeContainerStyle,
  getOriginalStatusColor
} from '../../styles/designSystem';

import { useKeycloak } from '@react-keycloak/web';

interface TabActionItemsProps {
  projectId: string | undefined;
  token: string | undefined;
  projectData: ProjectInterface,
  tokenParsed: KeycloakTokenParsed | undefined;
  onProjectUpdate?: () => Promise<void>;
  actionItems: ProjectActionItem[];
  highlightedItemId?: string | null;
}

// Action Item row component in ProjectPlan2 style
const ActionItemRow = ({ 
  actionItem, 
  projectData, 
  onSubmit,
  onDelete,
  onProjectUpdate,
  isNew = false,
  isHighlighted = false
}: { 
  actionItem: ProjectActionItem, 
  projectData: ProjectInterface, 
  onSubmit: Function,
  onDelete: (actionItemId: string) => void,
  onProjectUpdate?: () => Promise<void>,
  isNew?: boolean,
  isHighlighted?: boolean
}) => {
  const [form, setForm] = useState({ ...actionItem });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [_originalData, setOriginalData] = useState({ ...actionItem });

  const { addUserToProject } = useUsersPage();
  const { keycloak: { token: _token } } = useKeycloak();

  useEffect(() => {
    setForm({ ...actionItem });
    setOriginalData({ ...actionItem });
  }, [actionItem]);

  // Function to save changes (from old ActionItems)
  const saveChanges = async (updatedForm: any) => {
    setIsSaving(true);
    try {
      await onSubmit(updatedForm);
      setOriginalData({ ...updatedForm });
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    
    // Auto-save for certain fields immediately (ProjectPlan2 style)
    if (['status', 'priority', 'owner_id', 'due_date'].includes(field)) {
      const updatedForm = { ...form, [field]: value };
      saveChanges(updatedForm);
    }
  };

  // Function to handle text field blur events (ProjectPlan2 style)
  const handleFieldBlur = (field: string, value: any) => {
    const updatedForm = { 
      ...form, 
      [field]: value 
    };
    
    setForm(updatedForm);
    saveChanges(updatedForm);
  };

  const handleUserAdd = async (userId: string) => {
    const user = projectData?.users?.find(u => u.id === userId);
    if (user) {
      const userInProject = !!projectData.users?.find((u: ProjectUserInterface) => u.id === userId);
      if (!userInProject) {
        await addUserToProject(projectData.id || '', user);
      }
      handleFieldChange('owner_id', userId);
    }
  };



  return (
    <div className={`group hover:bg-gray-50/50 transition-all duration-200 ${
      isNew ? 'bg-blue-50/30' : ''
    } ${
      isHighlighted ? 'bg-yellow-50/50 border-l-4 border-yellow-400' : ''
    }`}>
              {/* Main Row - completely in ProjectPlan2 style */}
      <div className="flex items-center px-6 py-4 relative" style={{ borderBottom: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
        {/* Clickable Area for Expand */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        />
        
        {/* Expand Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="mr-4 p-1 rounded-md hover:bg-gray-100 transition-colors relative z-10"
        >
          <FaChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>

                  {/* Priority Level Indicator (like Risk Level in ProjectPlan2) */}
        <div className="mr-4 relative z-10 flex items-center space-x-2">
          <div className="relative">
            {/* Rotating Ring around priority dot - when saving any field */}
            {isSaving && (
              <div className={`absolute inset-0 w-5 h-5 -ml-1.5 -mt-1.5 rounded-full border-2 border-transparent animate-spin ${
                form.priority?.toLowerCase() === 'high' ? 'border-t-red-500/60 border-r-red-500/60' :
                form.priority?.toLowerCase() === 'medium' ? 'border-t-yellow-500/60 border-r-yellow-500/60' :
                form.priority?.toLowerCase() === 'low' ? 'border-t-green-500/60 border-r-green-500/60' :
                'border-t-gray-300/60 border-r-gray-300/60'
              }`} style={{ animation: 'spin 3s linear infinite' }} />
            )}
            {/* Priority Level Dot */}
            <FaCircle 
              className="w-2 h-2" 
              style={{ color: getRiskColor(form.priority || '') }}
            />
          </div>
        </div>

        {/* Action Item Name */}
        <div className="flex-1 min-w-0 relative z-10 flex items-center">
          <input
            type="text"
            value={form.name || ''}
            onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            onBlur={(e) => handleFieldBlur('name', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="flex-1 bg-transparent border-0 focus:outline-none placeholder-gray-400"
            style={getTaskNameStyle()}
            placeholder="Action item name..."
          />
        </div>

                  {/* Status (ProjectPlan2 style) */}
        <div className="mx-6 w-24 relative z-10 flex items-center">
          <select
            value={form.status || ''}
            onChange={(e) => {
              handleFieldChange('status', e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className={`${getOriginalStatusColor(form.status)}`}
            style={getOriginalStatusSelectStyle()}
          >
            <option value="NOT_STARTED">Not Started</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="ON_HOLD">On Hold</option>
            <option value="COMPLETED">Completed</option>
          </select>
        </div>

                  {/* Owner (ProjectPlan2 style) */}
        <div className="mx-6 w-32 relative z-10" style={getOriginalAssigneeContainerStyle()}>
          {(() => {
            const assignedUser = projectData?.users?.find(user => user.id === form.owner_id);
            if (assignedUser) {
              return (
                <Avatar
                  firstName={assignedUser.first_name}
                  lastName={assignedUser.last_name}
                  email={assignedUser.email}
                  size={20}
                  className="mr-2 flex-shrink-0"
                />
              );
            } else {
              return (
                <SelectUserAvatar
                  size={20}
                  className="mr-2 flex-shrink-0"
                />
              );
            }
          })()}
          <select
            value={form.owner_id || ''}
            onChange={(e) => handleUserAdd(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            style={{ ...getOriginalStatusSelectStyle(), flex: 1, minWidth: 0 }}
          >
            <option value="">Unassigned</option>
            {projectData?.users?.map((user) => (
              <option key={user.id} value={user.id}>
                {user.first_name} {user.last_name}
              </option>
            ))}
          </select>
        </div>

                  {/* Due Date (ProjectPlan2 style) */}
        <div className="mx-6 w-24 flex items-center text-xs text-gray-600 relative z-10">
          <input
            type="date"
            value={form.due_date ? new Date(form.due_date).toISOString().split('T')[0] : ''}
            onChange={(e) => {
              const value = e.target.value ? new Date(e.target.value).toISOString() : '';
              handleFieldChange('due_date', value);
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className="bg-transparent border-0 focus:outline-none text-xs w-full"
          />
        </div>

        {/* Priority */}
        <div className="mx-6 w-20 relative z-10">
          <select
            value={form.priority || ''}
            onChange={(e) => {
              handleFieldChange('priority', e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            className={`${getOriginalStatusColor(form.priority || '')}`}
            style={getOriginalStatusSelectStyle()}
          >
            <option value="LOW">Low</option>
            <option value="MEDIUM">Medium</option>
            <option value="HIGH">High</option>
          </select>
        </div>

                  {/* Documents (ProjectPlan2 style) */}
        <div className="ml-6 w-32 relative z-10">
                                  <ActionItemsDocumentAttachment
            projectId={form.project_id || ''}
            itemId={form.id || ''}
            documentUrl={form.document || undefined}
            fileName={form.file_name || undefined}
            onUpdate={onProjectUpdate}
          />
        </div>
      </div>

              {/* Expanded Details (completely ProjectPlan2 style) */}
      {isExpanded && (
        <div className="px-6 pb-6 pt-2" style={{ 
          backgroundColor: DesignSystem.colors.background.gray50,
          borderTop: `0.5px solid ${DesignSystem.colors.border.gray200}` 
        }}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Action Item Name and Description */}
            <div className="lg:col-span-2 space-y-4">
              {/* Action Item Name */}
              <div className="pt-2">
                <label className="block mb-2" style={getFormLabelStyle()}>Action Item Name</label>
                <input
                  type="text"
                  value={form.name || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  onBlur={(e) => handleFieldBlur('name', e.target.value)}
                  className="w-full text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter action item name..."
                />
              </div>
              
              {/* Description */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Description</label>
                <textarea
                  value={form.description || ''}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  onBlur={(e) => handleFieldBlur('description', e.target.value)}
                  className="w-full text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Add description..."
                  rows={6}
                  style={{ 
                    fontFamily: DesignSystem.typography.body.fontFamily,
                    fontSize: DesignSystem.typography.body.fontSize 
                  }}
                />
              </div>
            </div>

            {/* Action Item Details */}
            <div className="space-y-4">

              {/* Owner */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Owner</label>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={form.owner_id || ''}
                    onChange={(e) => handleUserAdd(e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="">Select owner</option>
                    {projectData?.users?.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Status</label>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={form.status || ''}
                    onChange={(e) => handleFieldChange('status', e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="NOT_STARTED">Not Started</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="ON_HOLD">On Hold</option>
                    <option value="COMPLETED">Completed</option>
                  </select>
                </div>
              </div>

              {/* Priority */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Priority</label>
                <div style={getFormSelectContainerStyle()}>
                  <select
                    value={form.priority || ''}
                    onChange={(e) => handleFieldChange('priority', e.target.value)}
                    style={getFormSelectStyle()}
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                </div>
              </div>

              {/* Due Date */}
              <div>
                <label className="block mb-2" style={getFormLabelStyle()}>Due Date</label>
                <div style={getFormSelectContainerStyle()}>
                  <input
                    type="date"
                    value={form.due_date ? new Date(form.due_date).toISOString().split('T')[0] : ''}
                    onChange={(e) => {
                      const value = e.target.value ? new Date(e.target.value).toISOString() : '';
                      handleFieldChange('due_date', value);
                    }}
                    className="w-full bg-transparent border-0 focus:outline-none"
                    style={{ fontSize: '14px' }}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="pt-2">
                <button
                  onClick={() => onDelete(actionItem.id!)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <FaTrash className="w-3 h-3" />
                  <span>Delete Action Item</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const TabActionItems = ({
  token,
  projectData,
  projectId,
  onProjectUpdate,
  actionItems,
  highlightedItemId,
}: TabActionItemsProps) => {
  // Add cleanup flag to prevent DOM operations after unmount
  const [isUnmounted, setIsUnmounted] = useState(false);
  // Filters and search (ProjectPlan2 style)
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [showAddActionItem, setShowAddActionItem] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll detection
  useEffect(() => {
    if (isUnmounted) return;

    const handleScroll = () => {
      if (!isUnmounted) {
      setIsScrolled(window.scrollY > 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isUnmounted]);

  // Cleanup effect to prevent DOM operations after unmount
  useEffect(() => {
    return () => {
      setIsUnmounted(true);
    };
  }, []);

  const updateActionItemHandler = async (form: ProjectActionItem) => {
    try {
      if (token && projectId) {
        await updateProjectActionItems(token, projectId, form.id, form);
        toast.success('Action item updated successfully');
        
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
      }
    } catch (error: any) {
      console.error('Error updating action item:', error);
      toast.error(error.message || 'Failed to update action item');
    }
  };

  const handleAddActionItem = async (actionItemData: any) => {
    try {
      if (token && projectId) {
        await createProjectActionItems(token, projectId, actionItemData);
        toast.success('Action item added successfully');
        setShowAddActionItem(false);
        
        if (onProjectUpdate) {
          await onProjectUpdate();
        }
      }
    } catch (error: any) {
      console.error('Error adding action item:', error);
      toast.error(error.message || 'Failed to add action item');
    }
  };

  const deleteActionItemHandler = async (actionItemId: string) => {
    if (!token || !projectId) return;
    
    if (!window.confirm('Are you sure you want to delete this action item?')) return;
    
    try {
      await deleteProjectActionItems(token, projectId, actionItemId);
      toast.success('Action item deleted successfully');
      
      if (onProjectUpdate) {
        await onProjectUpdate();
      }
    } catch (error: any) {
      console.error('Error deleting action item:', error);
      toast.error(error.message || 'Failed to delete action item');
    }
  };

  const getStatusCount = (status: string) => {
    return actionItems.filter(item => item.status === status).length;
  };

  // Filter and search logic
  const filteredActionItems = useMemo(() => {
    let filtered = actionItems;

    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.status === statusFilter);
    }

    if (userFilter !== 'all') {
      filtered = filtered.filter(item => item.owner_id === userFilter);
    }

    if (priorityFilter !== 'all') {
      filtered = filtered.filter(item => item.priority === priorityFilter);
    }

    return filtered;
  }, [actionItems, searchQuery, statusFilter, userFilter, priorityFilter]);

  return (
    <div className="min-h-screen bg-white" style={{ position: 'relative', zIndex: 1 }}>
      {/* Header with Search, Actions and Table Header - Fixed Sticky Section */}
      <div 
        className="sticky top-0"
        style={{ 
          zIndex: 10,
          position: 'sticky',
          backgroundColor: 'white'
        }}
      >
        <div className="max-w-[1280px] mx-auto px-0 py-4">
          <div className={`bg-white rounded-lg transition-shadow duration-200 ${isScrolled ? 'shadow-md' : ''}`} style={{ border: `0.5px solid ${DesignSystem.colors.border.gray200}` }}>
            {/* Search and Actions Row */}
            <div className="px-6 py-4" style={{ borderBottom: '0.5px solid #E5E7EB' }}>
              <div className="flex items-center justify-between">
                {/* Left side - Search and Quick Stats */}
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search action items..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2.5 w-80 text-sm bg-gray-50 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all"
                    />
                  </div>
                  
                  {/* Quick Stats */}
                  <div className="flex items-center space-x-4 text-sm">
                    <span className="text-gray-500">
                      {filteredActionItems.length} of {actionItems.length} items
                    </span>
                    <div className="w-px h-4 bg-gray-300"></div>
                    <span className="text-gray-500">
                      {getStatusCount('COMPLETED')} completed
                    </span>
                  </div>
                </div>

                {/* Right side - Actions */}
                <div className="flex items-center space-x-3">
                  {/* Filters */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all ${
                      showFilters 
                        ? 'bg-blue-50 text-blue-600 border border-blue-200' 
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-transparent'
                    }`}
                  >
                    <FaFilter className="w-3.5 h-3.5" />
                    <span>Filter</span>
                    {(statusFilter !== 'all' || userFilter !== 'all' || priorityFilter !== 'all') && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                        {[statusFilter, userFilter, priorityFilter].filter(f => f !== 'all').length}
                      </span>
                    )}
                  </button>

                  {/* Add Action Item */}
                    <button
                      onClick={() => setShowAddActionItem(true)}
                      className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm"
                    >
                      <FaPlus className="w-3.5 h-3.5" />
                      <span>Add Action Item</span>
                    </button>
                </div>
              </div>

              {/* Filter Panel */}
              {showFilters && (
                <div className="mt-4 pt-4" style={{ borderTop: '0.5px solid #E5E7EB' }}>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Status</option>
                        <option value="NOT_STARTED">Not Started</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="COMPLETED">Completed</option>
                      </select>
                    </div>
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={userFilter}
                        onChange={(e) => setUserFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Owners</option>
                        {projectData?.users?.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.first_name} {user.last_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={getFormSelectContainerStyle()}>
                      <select
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        style={getFormSelectStyle()}
                      >
                        <option value="all">All Priority</option>
                        <option value="LOW">Low</option>
                        <option value="MEDIUM">Medium</option>
                        <option value="HIGH">High</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Active Filters & Clear */}
                  {(statusFilter !== 'all' || userFilter !== 'all' || priorityFilter !== 'all') && (
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Active filters:</span>
                        {statusFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md">
                            Status: {statusFilter.replace('_', ' ')}
                            <button onClick={() => setStatusFilter('all')} className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                          </span>
                        )}
                        {userFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md">
                            User: {projectData?.users?.find(u => u.id === userFilter)?.first_name}
                            <button onClick={() => setUserFilter('all')} className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                          </span>
                        )}
                        {priorityFilter !== 'all' && (
                          <span className="inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md">
                            Priority: {priorityFilter}
                            <button onClick={() => setPriorityFilter('all')} className="ml-1 text-blue-500 hover:text-blue-700">×</button>
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setStatusFilter('all');
                          setUserFilter('all');
                          setPriorityFilter('all');
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Table Header */}
            <div className="px-6 py-2">
              <div className="flex items-center text-xs font-medium" style={getTableHeaderStyle()}>
                <div className="w-8"></div> {/* Expand button space */}
                <div className="w-4 mr-4"></div> {/* Priority indicator space */}
                <div className="flex-1" role="columnheader">Action Item</div>
                <div className="mx-6 w-24" role="columnheader">Status</div>
                <div className="mx-6 w-32" role="columnheader">Owner</div>
                <div className="mx-6 w-24" role="columnheader">Due</div>
                <div className="mx-6 w-20" role="columnheader">Priority</div>
                <div className="ml-6 w-32" role="columnheader">Docs</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Items List - Scrollable Content */}
      <div 
        className="max-w-[1280px] mx-auto px-0 -mt-4"
        style={{ 
          position: 'relative', 
          zIndex: 5,
          backgroundColor: 'white'
        }}
      >
        <div className="bg-white">
          {/* Action Item Rows */}
          <div>
            {filteredActionItems.sort((a: ProjectActionItem, b: ProjectActionItem) => a.id > b.id ? 1 : -1).map((actionItem) => (
              <ActionItemRow 
                key={actionItem.id} 
                actionItem={actionItem} 
                projectData={projectData}
                onSubmit={updateActionItemHandler}
                onDelete={deleteActionItemHandler}
                onProjectUpdate={onProjectUpdate}
                isHighlighted={highlightedItemId === actionItem.id}
              />
            ))}
          </div>

          {/* Empty State */}
          {filteredActionItems.length === 0 && (
            <div className="text-center py-16">
              <div className="text-gray-400 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-light text-gray-900 mb-2">No action items found</h3>
              <p className="text-gray-600 font-light mb-8">
                {searchQuery || statusFilter !== 'all' || userFilter !== 'all' || priorityFilter !== 'all'
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by creating your first action item.'
                }
              </p>
                <button
                  onClick={() => setShowAddActionItem(true)}
                  className="bg-white border border-gray-200 text-gray-600 px-6 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  Create First Action Item
                </button>
            </div>
          )}
        </div>
      </div>

      {/* Add Action Item Modal */}
      {showAddActionItem && projectId && (
        <AddActionItemModal
          isOpen={showAddActionItem}
          onClose={() => setShowAddActionItem(false)}
          onAddItem={handleAddActionItem}
          projectData={projectData}
          users={projectData?.users || []}
        />
      )}
    </div>
  );
}; 